import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { RenewalTask, updateTaskStatus } from '../src/Utils/renewalTaskApi';
import { toast } from 'sonner';
import {
  X,
  AlertCircle,
  CheckCircle,
  Clock,
  Calendar,
  User,
  Tag,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

interface TaskDetailsPanelProps {
  task: RenewalTask;
  onUpdate: () => void;
  onClose: () => void;
}

export const TaskDetailsPanel: React.FC<TaskDetailsPanelProps> = ({
  task,
  onUpdate,
  onClose,
}) => {
  const { user, token } = useAuth();

  // Map display status to backend status
  const mapDisplayToBackendStatus = (displayStatus: 'VALID' | 'EXPIRED' | 'PENDING'): RenewalTask['status'] => {
    if (displayStatus === 'VALID') return 'CLOSED';
    if (displayStatus === 'EXPIRED') return 'OPEN';
    return 'IN_PROGRESS';
  };

  // Map backend status to display status
  const mapBackendToDisplayStatus = (status: RenewalTask['status'], taskBucket?: string, dueDate?: string): 'VALID' | 'EXPIRED' | 'PENDING' => {
    if (status === 'CLOSED' || status === 'RESOLVED') return 'VALID';
    if (status === 'OPEN' && (taskBucket === 'OVERDUE' || (dueDate && new Date(dueDate) < new Date()))) return 'EXPIRED';
    return 'PENDING';
  };

  const [newStatus, setNewStatus] = useState<RenewalTask['status']>(task.status);
  const [newDisplayStatus, setNewDisplayStatus] = useState<'VALID' | 'EXPIRED' | 'PENDING'>(() => mapBackendToDisplayStatus(task.status, task.bucket, task.dueDate));
  const [updateComment, setUpdateComment] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'details' | 'checklist'>('details');
  const [documents, setDocuments] = useState<RenewalTask['documents']>(
    task.documents && task.documents.length > 0
      ? task.documents
      : [
        {
          id: 'doc-license',
          type: 'Professional License',
          required: true,
          status: 'PENDING',
          section: 'Intake/ Annual',
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        },
        {
          id: 'doc-cpr',
          type: 'CPR Certification',
          required: true,
          status: 'PENDING',
          section: 'Intake/ Annual',
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        },
      ]
  );

  // Update status when task changes
  useEffect(() => {
    setNewStatus(task.status);
    setNewDisplayStatus(mapBackendToDisplayStatus(task.status, task.bucket, task.dueDate));
    setUpdateComment('');
    setDocuments(
      task.documents && task.documents.length > 0
        ? task.documents
        : documents
    );
  }, [task.id, task.status, task.bucket, task.dueDate]);

  const handleStatusUpdate = async () => {
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    const backendStatus = mapDisplayToBackendStatus(newDisplayStatus);
    if (backendStatus === task.status) {
      toast.info('Status unchanged');
      return;
    }

    setIsUpdating(true);
    try {
      const response = await updateTaskStatus(
        task.id,
        {
          status: backendStatus,
          details: {
            comment: updateComment,
            updatedBy: user?.email || user?.id,
          },
        },
        token
      );

      if (response.success) {
        toast.success('Task status updated successfully');
        setUpdateComment('');
        onUpdate();
      } else {
        toast.error('Failed to update task status');
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      toast.error(error.message || 'Failed to update task status');
    } finally {
      setIsUpdating(false);
    }
  };

  const docStatus = (doc: NonNullable<RenewalTask['documents']>[number]) => doc.status || 'PENDING';

  const sendEmailNotification = (event: 'UPLOAD' | 'APPROVED' | 'REJECTED', target?: string) => {
    // Placeholder for backend email hook; currently logs and shows toast.
    console.info('Notification event', event, 'to', target || 'supervisor');
  };

  const handleUpload = (docId: string, file?: File) => {
    if (!file) return;
    setDocuments((prev) =>
      (prev || []).map((d) =>
        (d.id || d.type) === docId
          ? {
            ...d,
            status: 'PENDING',
            fileName: file.name,
            uploadedBy: user?.email || user?.id || 'user',
            uploadedAt: new Date().toISOString(),
          }
          : d
      )
    );
    toast.success('File uploaded');
    sendEmailNotification('UPLOAD', task.owner?.email);
  };

  const handleReview = (docId: string, decision: 'VALID' | 'EXPIRED', comment?: string) => {
    setDocuments((prev) =>
      (prev || []).map((d) =>
        (d.id || d.type) === docId
          ? {
            ...d,
            status: decision,
            reviewedBy: user?.email || user?.id || 'supervisor',
            reviewedAt: new Date().toISOString(),
            comment: comment || d.comment,
          }
          : d
      )
    );
    toast.success(decision === 'VALID' ? 'Document approved' : 'Document rejected');
    sendEmailNotification(decision === 'VALID' ? 'APPROVED' : 'REJECTED', task.owner?.email);
  };



  // Normalize section names to match the table format
  const normalizeSectionName = (section: string): string => {
    const normalized = section.trim();
    // Map common variations to the correct format
    const sectionMap: Record<string, string> = {
      'Intake / Annual': 'Intake/ Annual',
      'Intake/Annual': 'Intake/ Annual',
      'Intake /Annual': 'Intake/ Annual',
      'Assessments': 'Assessments:',
      'DX / Eligibility': 'DX / Eligibility',
      'DX/Eligibility': 'DX / Eligibility',
      'DX /Eligibility': 'DX / Eligibility',
      'DX/Eligiblity': 'DX / Eligibility',
    };
    return sectionMap[normalized] || normalized;
  };

  const renderChecklist = () => (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold">Documents & Checklist</h3>
      <div className="space-y-4">
        {Object.entries(
          (documents || []).reduce<Record<string, NonNullable<RenewalTask['documents']>>>((acc, doc) => {
            const rawSection = doc.section || 'Other';
            const normalizedSection = normalizeSectionName(rawSection);
            if (!acc[normalizedSection]) acc[normalizedSection] = [];
            acc[normalizedSection].push(doc);
            return acc;
          }, {})
        ).map(([section, docs]) => (
          <div key={section} className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">{section}</p>
              <Badge variant="outline" className="text-[10px]">
                {docs.length} item{docs.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            {docs
              .slice()
              .sort((a, b) => {
                const statusOrder = (s: string) => (s === 'EXPIRED' ? 0 : s === 'PENDING' ? 1 : 2);
                const diff = statusOrder(docStatus(a)) - statusOrder(docStatus(b));
                if (diff !== 0) return diff;
                const aEnd = a.endDate ? new Date(a.endDate).getTime() : Infinity;
                const bEnd = b.endDate ? new Date(b.endDate).getTime() : Infinity;
                return aEnd - bEnd;
              })
              .map((doc) => {
                const status = docStatus(doc);
                const now = new Date();
                const end = doc.endDate ? new Date(doc.endDate) : null;
                const daysToExpiry = end ? Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
                const renewalCue =
                  daysToExpiry !== null && daysToExpiry >= 0 && [3, 7, 10, 15].find((d) => daysToExpiry <= d);
                const badgeVariant =
                  status === 'VALID' ? 'default' : status === 'EXPIRED' ? 'destructive' : 'outline';
                return (
                  <div key={doc.id || doc.type} className="border rounded-md p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{doc.type}</p>
                        <p className="text-xs text-gray-500">
                          {doc.required ? 'Required' : 'Optional'}
                          {doc.fileName ? ` • ${doc.fileName}` : ''}
                        </p>
                        {doc.startDate || doc.endDate ? (
                          <p className="text-xs text-gray-500 mt-1">
                            Validity: {doc.startDate ? format(new Date(doc.startDate), 'MMM dd, yyyy') : '—'} - {doc.endDate ? format(new Date(doc.endDate), 'MMM dd, yyyy') : '—'}
                          </p>
                        ) : null}
                        {renewalCue && (
                          <p className="text-[10px] text-amber-600">
                            Renewal due within {renewalCue} days ({daysToExpiry} days remaining)
                          </p>
                        )}
                        {status === 'EXPIRED' && (
                          <p className="text-[10px] text-red-600">Expired</p>
                        )}
                      </div>
                      <Badge variant={badgeVariant}>{status}</Badge>
                    </div>

                    <div className="flex flex-wrap gap-2 items-center">
                      <label className="text-xs text-blue-600 cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => handleUpload(doc.id || doc.type, e.target.files?.[0])}
                        />
                        Upload / Replace
                      </label>
                      {user?.roles?.includes('SUPERVISOR') && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleReview(doc.id || doc.type, 'VALID')}>
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleReview(doc.id || doc.type, 'EXPIRED')}>
                            Reject
                          </Button>
                        </>
                      )}
                    </div>

                    {doc.comment && (
                      <p className="text-xs text-gray-500">Comment: {doc.comment}</p>
                    )}
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </div>
  );

  const getStatusBadge = (status: RenewalTask['status']) => {
    const displayStatus = mapBackendToDisplayStatus(status, task.bucket, task.dueDate);
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      VALID: { variant: 'default', label: 'Valid' },
      EXPIRED: { variant: 'destructive', label: 'Expired' },
      PENDING: { variant: 'outline', label: 'Pending' },
    };

    const config = variants[displayStatus] || variants.PENDING;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: RenewalTask['priority']) => {
    const variants: Record<string, { className: string; label: string; icon: React.ReactNode }> = {
      P0: { className: 'bg-red-100 text-red-800 border-red-300', label: 'P0', icon: <AlertCircle className="w-3 h-3" /> },
      P1: { className: 'bg-orange-100 text-orange-800 border-orange-300', label: 'P1', icon: <AlertCircle className="w-3 h-3" /> },
      P2: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'P2', icon: <Clock className="w-3 h-3" /> },
      P3: { className: 'bg-blue-100 text-blue-800 border-blue-300', label: 'P3', icon: <CheckCircle className="w-3 h-3" /> },
      P4: { className: 'bg-gray-100 text-gray-800 border-gray-300', label: 'P4', icon: <CheckCircle className="w-3 h-3" /> },
    };

    const config = variants[priority] || variants.P4;
    return (
      <Badge variant="outline" className={`${config.className} flex items-center gap-1`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const getBucketLabel = (bucket: RenewalTask['bucket']) => {
    const labels: Record<string, string> = {
      OVERDUE: 'Overdue',
      D0_3: 'Due in 0-3 days',
      D4_7: 'Due in 4-7 days',
      D8_14: 'Due in 8-14 days',
      D15_30: 'Due in 15-30 days',
      D31_90: 'Due in 31-90 days',
      D90_PLUS: 'Due in 90+ days',
    };
    return labels[bucket] || bucket;
  };

  const formatUserName = (entry: { by?: string; actor?: string; details?: any }) => {
    const userId = entry.by || entry.actor;
    if (!userId) return 'Unknown';

    // Helper to format email to name
    const formatEmailToName = (email: string): string => {
      const emailPart = email.split('@')[0];
      if (!emailPart) return 'User';
      return emailPart
        .split(/[._-]/)
        .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
    };

    // If it's an email, extract the name part (before @) and format it
    if (userId.includes('@')) {
      return formatEmailToName(userId);
    }

    // Check details for user information
    if (entry.details && typeof entry.details === 'object') {
      const details = entry.details;
      if (details.updatedBy && typeof details.updatedBy === 'string' && details.updatedBy.includes('@')) {
        return formatEmailToName(details.updatedBy);
      }
      if (details.reviewedBy && typeof details.reviewedBy === 'string' && details.reviewedBy.includes('@')) {
        return formatEmailToName(details.reviewedBy);
      }
      if (details.userName && typeof details.userName === 'string') {
        return details.userName;
      }
      if (details.name && typeof details.name === 'string') {
        return details.name;
      }
    }

    // If it's just an ID, return "User" instead of the raw ID
    return 'User';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-6 border-b sticky top-0 bg-white z-10" style={{ height: '120px' }}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {getStatusBadge(task.status)}
              {getPriorityBadge(task.priority)}
            </div>
            <h2 className="text-xl font-semibold truncate">{task.itemName}</h2>
            <p className="text-sm text-gray-600 mt-1">{task.sectionName}</p>
          </div>
          <div className="flex items-center gap-2">
            {task.itemCode && (
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm font-medium">{task.itemCode}</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={(value) => setSelectedTab(value as 'details' | 'checklist')} className="flex flex-col flex-1">
        <div className="px-6 pt-4">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="checklist">Checklist</TabsTrigger>
          </TabsList>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-6 p-6">
            <TabsContent value="details" className="mt-0">
              {/* Assigned To & Summary Section */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">Assigned To & Summary</h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div className="space-y-3">
                    {task.owner && (
                      <div>
                        <Label className="text-xs text-gray-500">Assigned To</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">
                              {task.owner.firstName} {task.owner.lastName}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{task.owner.email}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <Label className="text-xs text-gray-500">Audit Date Range</Label>
                      <div className="mt-1">
                        {task.startDate && task.endDate ? (
                          <span className="text-sm">
                            {format(new Date(task.startDate), 'MM.dd.yyyy')} - {format(new Date(task.endDate), 'MM.dd.yyyy')}
                          </span>
                        ) : task.startDate ? (
                          <span className="text-sm">
                            {format(new Date(task.startDate), 'MM.dd.yyyy')} - —
                          </span>
                        ) : task.endDate ? (
                          <span className="text-sm">
                            — - {format(new Date(task.endDate), 'MM.dd.yyyy')}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">Not specified</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-gray-500">Due Date</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        {task.dueDate && !isNaN(new Date(task.dueDate).getTime()) ? (
                          <span className="text-sm">{format(new Date(task.dueDate), 'MMM dd, yyyy')}</span>
                        ) : (
                          <span className="text-sm text-gray-400">No due date</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs text-gray-500">Bucket</Label>
                      <div className="mt-1">
                        <Badge variant="outline" className="text-xs">{getBucketLabel(task.bucket)}</Badge>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reason - Full Width */}
                <div>
                  <Label className="text-xs text-gray-500">Reason</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded-md">
                    <p className="text-sm">{task.reason}</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="checklist" className="mt-0">
              {renderChecklist()}
            </TabsContent>
          </div>
        </div>
      </Tabs>

      {/* Status Update Component */}
      <div className="p-6 border-t">
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold mb-3">Status Update</h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="status" className="text-xs">Current Status</Label>
                  <div>{getStatusBadge(task.status)}</div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="new-status" className="text-xs">New Status</Label>
                  <Select
                    value={newDisplayStatus}
                    onValueChange={(value) => setNewDisplayStatus(value as 'VALID' | 'EXPIRED' | 'PENDING')}
                  >
                    <SelectTrigger id="new-status" className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="VALID">Valid</SelectItem>
                      <SelectItem value="EXPIRED">Expired</SelectItem>
                      <SelectItem value="PENDING">Pending</SelectItem>
                      <SelectItem value="CLOSED">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Right Column - Comment */}
              <div className="space-y-1.5">
                <Label htmlFor="comment" className="text-xs">Comment (Optional)</Label>
                <Textarea
                  id="comment"
                  placeholder="Add a comment..."
                  value={updateComment}
                  onChange={(e) => setUpdateComment(e.target.value)}
                  rows={4}
                  className="text-sm resize-none"
                />
              </div>
            </div>

            {/* Action Button and Preview - Full Width */}
            <div className="mt-3 space-y-2">
              <Button
                onClick={handleStatusUpdate}
                disabled={isUpdating || mapDisplayToBackendStatus(newDisplayStatus) === task.status}
                className="w-full"
                size="sm"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Status'
                )}
              </Button>

              {mapDisplayToBackendStatus(newDisplayStatus) !== task.status && (
                <div className="p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-800">
                    Status will change from <strong>{mapBackendToDisplayStatus(task.status, task.bucket, task.dueDate)}</strong> to <strong>{newDisplayStatus}</strong>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Audit Trail Component */}
          {task.auditTrail && task.auditTrail.length > 0 && (
            <div className="space-y-4 pt-2 border-t">
              <div>
                <h3 className="text-sm font-semibold mb-3">Audit Trail</h3>

                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {task.auditTrail.map((entry, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1" />
                        {index < task.auditTrail!.length - 1 && (
                          <div className="w-px h-full bg-gray-200 mt-1 min-h-[40px]" />
                        )}
                      </div>
                      <div className="flex-1 pb-2 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="font-medium text-xs">{entry.action}</div>
                          <div className="text-xs text-gray-500 whitespace-nowrap ml-2">
                            {entry.at && !isNaN(new Date(entry.at).getTime())
                              ? format(new Date(entry.at), 'MMM dd, HH:mm')
                              : entry.timestamp && !isNaN(new Date(entry.timestamp).getTime())
                                ? format(new Date(entry.timestamp), 'MMM dd, HH:mm')
                                : 'Invalid date'}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mb-1">By: {formatUserName(entry)}</div>
                        {entry.details && (
                          <div className="mt-1 p-2 bg-gray-50 rounded text-xs">
                            <pre className="whitespace-pre-wrap break-words">
                              {typeof entry.details === 'string'
                                ? entry.details
                                : JSON.stringify(entry.details, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

